import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Eye, Brain, Gauge, Users, Cpu, CheckCircle, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

// Five capability pillars
const capabilities = [
  {
    title: "Sense & Understand",
    description: "Turn operational data and observations into a useful picture of what is happening.",
    icon: Eye,
  },
  {
    title: "Model & Evaluate",
    description: "Test scenarios, quantify uncertainty and compare capability before committing resources.",
    icon: Brain,
  },
  {
    title: "Decide & Optimise",
    description: "Support decisions by comparing options, constraints, confidence and consequences.",
    icon: Gauge,
  },
  {
    title: "Coordinate & Control",
    description: "Bring assets, information and operators into a governed mission workflow.",
    icon: Users,
  },
  {
    title: "Autonomy & Integration",
    description: "Apply bounded autonomous functions to compatible platforms and integrate them into the wider system.",
    icon: Cpu,
  },
];

// Solutions
const solutions = [
  {
    title: "Defence & Counter-UAS",
    description: "Analytical assessment, mission integration, autonomy and selected airframe programmes.",
    href: "/solutions/defence-cuas",
  },
  {
    title: "Protective Security",
    description: "Continuity of situational awareness and operator-controlled aerial observation through Helais Sentinel.",
    href: "/solutions/protective-security",
  },
  {
    title: "Public Safety & Emergency Response",
    description: "Mission orchestration and aerial support workflows through Helais.",
    href: "/solutions/public-safety",
  },
  {
    title: "Civil Operational Intelligence",
    description: "Operational analytics from existing infrastructure through UrbanAnalytix.",
    href: "/solutions/civil-intelligence",
  },
];

// Platforms
const platforms = [
  {
    name: "UAVSimNet",
    description: "Simulation and decision support for counter-UAS capability assessment",
    href: "https://uavsimnet.com",
    external: true,
  },
  {
    name: "AI-AV",
    description: "Vision-based target-oriented adaptive control for autonomous platforms",
    href: "/platforms/ai-av",
  },
  {
    name: "Helais",
    description: "Mission orchestration and aerial-operations platform",
    href: "/platforms/helais",
  },
  {
    name: "UrbanAnalytix",
    description: "Operational intelligence from existing infrastructure",
    href: "https://urbananalytix.co.uk",
    external: true,
  },
];

// Operating model
const operatingModel = [
  "Start with the operational requirement",
  "Retain existing customer infrastructure where practical",
  "Integrate qualified partner equipment through governed interfaces",
  "Apply RLK software and IP where it creates differentiated value",
  "Keep human authority, evidence and configuration control explicit",
  "Expand modularly as requirements mature",
];

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const cardVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
  }
};

export default function HomePage() {
  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-background" />
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 scan-line" />
        
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-[800px] h-[800px] opacity-30">
            <div className="radar-sweep" />
          </div>
        </div>
        
        <div className="orb-glow w-[600px] h-[600px] -top-[200px] -right-[200px]" />
        <div className="orb-glow w-[400px] h-[400px] bottom-[10%] -left-[100px]" />
        <div className="absolute inset-0 noise-texture" />
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
          <motion.div 
            className="flex flex-col items-center text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="mb-10" variants={cardVariant}>
              <img
                src="https://customer-assets.emergentagent.com/job_rlk-modern/artifacts/wepugaac_RLK%20Grey%20group.png"
                alt="The RLK Group"
                className="h-14 sm:h-16 lg:h-20 w-auto mx-auto"
              />
            </motion.div>
            
            <motion.h1 
              className="text-display text-foreground mb-6"
              variants={cardVariant}
            >
              Intelligence and autonomy<br className="hidden sm:block" /> for critical operations
            </motion.h1>
            
            <motion.p 
              className="text-subtitle max-w-3xl mb-12"
              variants={cardVariant}
            >
              The RLK Group develops operational intelligence, mission software and autonomy technologies for defence, public safety and critical infrastructure. From simulation and evidence to multi-asset coordination and platform-level autonomy, we help organisations understand difficult environments and act with greater control.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 mb-8"
              variants={cardVariant}
            >
              <Link
                to="/solutions"
                className="btn-primary"
                data-testid="explore-solutions-btn"
              >
                Explore Solutions
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Link
                to="/platforms"
                className="btn-secondary"
                data-testid="explore-platforms-btn"
              >
                Explore Platforms & Technology
              </Link>
            </motion.div>
            
            <motion.div variants={cardVariant}>
              <Link
                to="/contact"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                Request a Briefing
                <ArrowUpRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
        
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/50">Scroll</span>
          <motion.div 
            className="w-px h-10 bg-gradient-to-b from-muted-foreground/30 to-transparent"
            animate={{ scaleY: [1, 0.6, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
        
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </section>

      {/* What We Do - Five Pillars */}
      <section className="section-padding-lg relative" aria-labelledby="capabilities-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">What We Do</p>
            <h2 id="capabilities-heading" className="text-headline text-foreground">
              Core Capabilities
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5 max-w-6xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.article 
                key={index} 
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-5">
                  <cap.icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <h3 className="text-label mb-2">{cap.title}</h3>
                <p className="text-sm text-muted-foreground">{cap.description}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="divider" role="separator" />
      </div>

      {/* Solutions Section */}
      <section className="section-padding-lg relative" aria-labelledby="solutions-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">Solutions</p>
            <h2 id="solutions-heading" className="text-headline text-foreground">
              Defined Customer Outcomes
            </h2>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto">
            {solutions.map((solution, index) => (
              <Link key={index} to={solution.href} className="group">
                <motion.article 
                  className="card-pillar h-full p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-title text-foreground mb-3 group-hover:text-foreground/90 transition-colors">
                    {solution.title}
                  </h3>
                  <p className="text-body mb-4">
                    {solution.description}
                  </p>
                  <span className="inline-flex items-center text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Learn more
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="divider" role="separator" />
      </div>

      {/* Platforms Section */}
      <section className="section-padding-lg relative bg-card/30 edge-glow" aria-labelledby="platforms-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">Platforms</p>
            <h2 id="platforms-heading" className="text-headline text-foreground">
              Principal Technologies
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
            {platforms.map((platform, index) => {
              const CardContent = (
                <motion.article 
                  className="card-premium h-full p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-label text-foreground mb-2 group-hover:text-foreground/90 transition-colors flex items-center gap-2">
                    {platform.name}
                    {platform.external && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {platform.description}
                  </p>
                  <span className="inline-flex items-center text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                    {platform.external ? "Visit" : "Explore"}
                    <ArrowUpRight className="h-3 w-3 ml-1" />
                  </span>
                </motion.article>
              );

              return platform.external ? (
                <a key={index} href={platform.href} target="_blank" rel="noopener noreferrer" className="group">
                  {CardContent}
                </a>
              ) : (
                <Link key={index} to={platform.href} className="group">
                  {CardContent}
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/platforms/advanced-development" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors">
              View Advanced Development & Design Assets
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="divider" role="separator" />
      </div>

      {/* How RLK Works */}
      <section className="section-padding-lg relative" aria-labelledby="operating-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-caption mb-4">How RLK Works</p>
              <h2 id="operating-heading" className="text-headline text-foreground mb-6">
                Operating Model
              </h2>
              <p className="text-body mb-8">
                We design systems around your operational requirement, not around our product catalogue. Our approach integrates existing infrastructure, qualified partner equipment, and RLK technology to solve defined problems with traceable evidence.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ul className="space-y-4">
                {operatingModel.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="divider" role="separator" />
      </div>

      {/* Evidence Section */}
      <section className="section-padding-lg relative bg-card/30 edge-glow" aria-labelledby="evidence-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-caption mb-4">Evidence & Assurance</p>
            <h2 id="evidence-heading" className="text-headline text-foreground mb-6">
              Credibility Through Discipline
            </h2>
            <p className="text-body mb-8">
              Every RLK product comes with defined evidence, model provenance, and clear maturity indicators. We distinguish current capability from future roadmap and maintain human authority at every decision point.
            </p>
            <Link to="/evidence" className="btn-secondary">
              View Evidence & Assurance
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding-lg relative" aria-labelledby="cta-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 id="cta-heading" className="text-headline text-foreground mb-4">
              Bring us the operational problem
            </h2>
            <p className="text-subtitle mb-10">
              We will define the smallest credible system needed to solve it.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="discuss-requirement-btn">
              Discuss a Requirement
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
